import { IMarketingBanner } from "@/models/alertsBanners/alertsBanners.types"
import { FIELD_MAX_LENGTH } from "@/models/alertsBanners/marketingBannerConfig"

export interface IBannerCopy {
  title: string
  subtitle: string
  buttonText: string
  buttonLink: string
  illustrationName: string
}

export type BannerCopyField = keyof IBannerCopy
export type BannerCopyErrors = Partial<Record<BannerCopyField, string>>

const COPY_FIELDS: readonly BannerCopyField[] = [
  "title",
  "subtitle",
  "buttonText",
  "buttonLink",
  "illustrationName",
]

const SERVER_FIELD_MESSAGES: Record<BannerCopyField, string> = {
  title: `Enter a title of at most ${FIELD_MAX_LENGTH.title} characters`,
  subtitle: `Enter a subtitle of at most ${FIELD_MAX_LENGTH.subtitle} characters`,
  buttonText: `Give the button text of at most ${FIELD_MAX_LENGTH.buttonText} characters and a link, or clear both`,
  buttonLink:
    "Use a path starting with /, an https://so.energy link, an https://soenergy.typeform.com link or a tel: link",
  illustrationName: "Pick an illustration from the list",
}

const COPY_FIELD_NAMES: ReadonlySet<string> = new Set(COPY_FIELDS)

const isCopyField = (name: string): name is BannerCopyField =>
  COPY_FIELD_NAMES.has(name)

export const copyOf = (banner: IMarketingBanner): IBannerCopy => ({
  title: banner.title,
  subtitle: banner.body,
  buttonText: banner.buttonText ?? "",
  buttonLink: banner.buttonLink ?? "",
  illustrationName: banner.illustrationName,
})

const trimmed = (copy: IBannerCopy): IBannerCopy => ({
  title: copy.title.trim(),
  subtitle: copy.subtitle.trim(),
  buttonText: copy.buttonText.trim(),
  buttonLink: copy.buttonLink.trim(),
  illustrationName: copy.illustrationName,
})

const tooLong = (value: string, field: keyof typeof FIELD_MAX_LENGTH) =>
  value.length > FIELD_MAX_LENGTH[field]
    ? `Keep it to ${FIELD_MAX_LENGTH[field]} characters`
    : undefined

export const copyErrors = (copy: IBannerCopy): BannerCopyErrors => {
  const { title, subtitle, buttonText, buttonLink } = trimmed(copy)
  const errors: BannerCopyErrors = {
    title: title ? tooLong(title, "title") : "Enter a title",
    subtitle: subtitle ? tooLong(subtitle, "subtitle") : "Enter a subtitle",
    buttonText:
      !buttonText && buttonLink
        ? "Add button text, or clear the link too"
        : tooLong(buttonText, "buttonText"),
    buttonLink:
      buttonText && !buttonLink
        ? "Add a link, or clear the button text too"
        : tooLong(buttonLink, "buttonLink"),
    illustrationName: copy.illustrationName
      ? undefined
      : "Pick an illustration",
  }
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message),
  )
}

/** Only the fields that differ; clearing the button sends "" for both, never null. */
export const changedCopy = (
  before: IBannerCopy,
  after: IBannerCopy,
): Partial<IBannerCopy> => {
  const from = trimmed(before)
  const to = trimmed(after)
  return Object.fromEntries(
    COPY_FIELDS.filter((field) => from[field] !== to[field]).map((field) => [
      field,
      to[field],
    ]),
  )
}

export const serverFieldErrors = (fields: string[]): BannerCopyErrors =>
  Object.fromEntries(
    fields
      .filter(isCopyField)
      .map((field) => [field, SERVER_FIELD_MESSAGES[field]]),
  )
